import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

const TOKEN_KEY = "token";
const USER_ID_KEYS = ["usuarioId", "userId"];
const ROLE_KEY = "userRole";
const EMAIL_KEY = "userEmail";

export const getAuthToken = () =>
  Cookies.get(TOKEN_KEY) ||
  localStorage.getItem(TOKEN_KEY) ||
  sessionStorage.getItem(TOKEN_KEY) ||
  "";

export const decodeToken = (token = getAuthToken()) => {
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

export const getUserIdFromPayload = (payload = {}) =>
  payload.id || payload._id || payload.userId || payload.usuarioId || payload.sub || "";

export const getStoredUserId = () => {
  for (const key of USER_ID_KEYS) {
    const value = localStorage.getItem(key) || sessionStorage.getItem(key) || Cookies.get(key);
    if (value) return value;
  }
  return "";
};

export const persistSession = ({ token, user, remember = false } = {}) => {
  if (!token) return null;

  const payload = decodeToken(token) || {};
  const userId =
    getUserIdFromPayload(payload) ||
    user?._id ||
    user?.id ||
    user?.userId ||
    user?.usuarioId ||
    "";

  const role = payload.role || user?.role || "";
  const email = payload.email || payload.sub || user?.email || "";

  const cookieOptions = {
    expires: remember ? 7 : 1,
    sameSite: "Lax",
    secure: window.location.protocol === "https:",
    path: "/",
  };

  Cookies.set(TOKEN_KEY, token, cookieOptions);
  if (userId) Cookies.set("userId", userId, cookieOptions);

  localStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(TOKEN_KEY, token);

  if (userId) {
    localStorage.setItem("usuarioId", userId);
    localStorage.setItem("userId", userId);
    sessionStorage.setItem("usuarioId", userId);
    sessionStorage.setItem("userId", userId);
  }

  if (role) {
    localStorage.setItem(ROLE_KEY, role);
    sessionStorage.setItem(ROLE_KEY, role);
  }

  if (email) {
    localStorage.setItem(EMAIL_KEY, email);
    sessionStorage.setItem(EMAIL_KEY, email);
  }

  window.dispatchEvent(new Event("dqpa:session-updated"));

  return {
    token,
    id: userId,
    role,
    email,
    payload,
  };
};

export const getSessionUser = () => {
  const token = getAuthToken();
  const payload = decodeToken(token) || {};
  const storedUserId = getStoredUserId();
  const id = storedUserId || getUserIdFromPayload(payload);
  const role = localStorage.getItem(ROLE_KEY) || sessionStorage.getItem(ROLE_KEY) || payload.role || "";
  const email = localStorage.getItem(EMAIL_KEY) || sessionStorage.getItem(EMAIL_KEY) || payload.email || payload.sub || "";

  if (!token && !id) return null;

  if (token && id) {
    localStorage.setItem("usuarioId", id);
    localStorage.setItem("userId", id);
  }
  if (role) localStorage.setItem(ROLE_KEY, role);
  if (email) localStorage.setItem(EMAIL_KEY, email);

  return {
    token,
    id,
    role,
    email,
    payload,
  };
};

export const requireSessionUser = () => {
  const session = getSessionUser();

  if (!session?.token || !session?.id) {
    throw new Error("No se ha encontrado la sesión del usuario.");
  }

  return session;
};

export const clearSession = () => {
  Cookies.remove(TOKEN_KEY, { path: "/" });
  Cookies.remove("userId", { path: "/" });

  document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
  document.cookie = "userId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";

  [
    TOKEN_KEY,
    "usuarioId",
    "userId",
    ROLE_KEY,
    EMAIL_KEY,
  ].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(new Event("dqpa:session-updated"));
};

export const isTokenExpired = (token = getAuthToken()) => {
  const payload = decodeToken(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 <= Date.now();
};

export const getTokenExpirationMs = (token = getAuthToken()) => {
  const payload = decodeToken(token);
  return payload?.exp ? payload.exp * 1000 : null;
};
